/**
 * Drawer 'Nova/Editar senha' (Dialog largo, padrão resource-drawer):
 * Nome* → Descrição → Usuário → Senha* (gerador crypto + força + mostrar/
 * ocultar; no edit é opcional e mantém a atual) → URL (https://) → IP →
 * Pasta → Cliente. O segredo vive só no estado deste formulário e é zerado
 * no fechamento.
 */
import { useState, type FormEvent } from 'react';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import type { VaultEntry, VaultEntryBody, VaultFolder } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { useCreateVaultEntry, useUpdateVaultEntry } from './vault-mutations';
import { vaultErrorMessage } from './vault-format';

// ── Gerador (crypto.getRandomValues, sem viés de módulo) ────────────────────

const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITS = '0123456789';
const SYMBOLS = '!@#$%&*()-_=+[]{};:,.?/';

function generatePassword(opts: {
  length: number;
  upper: boolean;
  digits: boolean;
  symbols: boolean;
}): string {
  const pool =
    LOWER + (opts.upper ? UPPER : '') + (opts.digits ? DIGITS : '') + (opts.symbols ? SYMBOLS : '');
  const length = Math.min(64, Math.max(8, Math.floor(opts.length) || 20));
  // Rejection sampling: descarta valores acima do maior múltiplo de pool.length
  // representável em 32 bits para não enviesar o módulo.
  const limit = Math.floor(0x1_0000_0000 / pool.length) * pool.length;
  const out: string[] = [];
  const buf = new Uint32Array(length * 2);
  while (out.length < length) {
    crypto.getRandomValues(buf);
    for (const n of buf) {
      if (n >= limit) continue;
      out.push(pool.charAt(n % pool.length));
      if (out.length === length) break;
    }
  }
  return out.join('');
}

/** Entropia aproximada: comprimento × log2(tamanho do alfabeto presente). */
function estimateBits(secret: string): number {
  let pool = 0;
  if (/[a-z]/.test(secret)) pool += 26;
  if (/[A-Z]/.test(secret)) pool += 26;
  if (/[0-9]/.test(secret)) pool += 10;
  if (/[^a-zA-Z0-9]/.test(secret)) pool += 32;
  return pool === 0 ? 0 : secret.length * Math.log2(pool);
}

function StrengthBar({ secret }: { secret: string }) {
  const bits = estimateBits(secret);
  const level =
    bits < 45
      ? { label: 'fraca', bar: 'bg-destructive', text: 'text-destructive' }
      : bits < 75
        ? { label: 'média', bar: 'bg-warning', text: 'text-warning' }
        : { label: 'forte', bar: 'bg-success', text: 'text-success' };
  const pct = Math.min(100, Math.round((bits / 90) * 100));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full transition-all', level.bar)} style={{ width: `${pct}%` }} />
      </div>
      <span className={cn('shrink-0 text-[11px] font-medium', level.text)}>
        Senha {level.label}
      </span>
    </div>
  );
}

function GeneratorPopover({ onGenerate }: { onGenerate: (secret: string) => void }) {
  const [open, setOpen] = useState(false);
  const [length, setLength] = useState(20);
  const [upper, setUpper] = useState(true);
  const [digits, setDigits] = useState(true);
  const [symbols, setSymbols] = useState(true);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 text-[13px] font-medium text-primary transition-colors hover:text-primary-pressed"
        >
          <RefreshCw className="size-3.5" />
          Gerador
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="vault-gen-length" className="text-[13px]">
              Comprimento
            </Label>
            <Input
              id="vault-gen-length"
              type="number"
              min={8}
              max={64}
              value={length}
              onChange={(e) => setLength(Number(e.target.value))}
              className="h-7 w-16 px-2 text-sm tabular-nums"
            />
          </div>
          <input
            type="range"
            min={8}
            max={64}
            value={Math.min(64, Math.max(8, length || 8))}
            onChange={(e) => setLength(Number(e.target.value))}
            aria-label="Comprimento da senha"
            className="w-full accent-primary"
          />
        </div>

        <div className="space-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={upper} onCheckedChange={(c) => setUpper(c === true)} />
            Maiúsculas (A–Z)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={digits} onCheckedChange={(c) => setDigits(c === true)} />
            Números (0–9)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={symbols} onCheckedChange={(c) => setSymbols(c === true)} />
            Símbolos (!@#…)
          </label>
        </div>

        <Button
          type="button"
          size="sm"
          className="w-full"
          onClick={() => {
            onGenerate(generatePassword({ length, upper, digits, symbols }));
            setOpen(false);
          }}
        >
          Gerar
        </Button>
      </PopoverContent>
    </Popover>
  );
}

// ── Pastas: options com indentação por profundidade ─────────────────────────

function folderOptions(folders: VaultFolder[]): Array<{ id: number; label: string }> {
  const byParent = new Map<number | null, VaultFolder[]>();
  for (const f of folders) {
    const list = byParent.get(f.parentId) ?? [];
    list.push(f);
    byParent.set(f.parentId, list);
  }
  const out: Array<{ id: number; label: string }> = [];
  const walk = (parentId: number | null, depth: number) => {
    if (depth > folders.length) return; // proteção contra ciclos
    const children = (byParent.get(parentId) ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    for (const f of children) {
      out.push({ id: f.id, label: `${'   '.repeat(depth)}${f.name}` });
      walk(f.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

// ── Drawer ──────────────────────────────────────────────────────────────────

export function VaultEntryDrawer({
  vaultId,
  folders,
  defaultFolderId,
  initial,
  onClose,
}: {
  vaultId: number;
  folders: VaultFolder[];
  defaultFolderId: number | null;
  initial?: VaultEntry;
  onClose: () => void;
}) {
  const isEdit = initial !== undefined;

  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [username, setUsername] = useState(initial?.username ?? '');
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [url, setUrl] = useState(initial?.url ?? '');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [ip, setIp] = useState(initial?.ip ?? '');
  const [folderId, setFolderId] = useState(() => {
    const id = isEdit ? initial.folderId : defaultFolderId;
    return id !== null ? String(id) : '';
  });
  const [client, setClient] = useState<ClientRef | null>(
    initial?.clientId != null
      ? { id: initial.clientId, name: initial.clientName ?? `Cliente #${initial.clientId}` }
      : null
  );

  const create = useCreateVaultEntry(vaultId);
  const update = useUpdateVaultEntry();
  const mutation = isEdit ? update : create;

  // Segurança: zera o segredo do estado antes de desmontar.
  const close = () => {
    setSecret('');
    onClose();
  };

  const valid = name.trim() !== '' && (isEdit || secret !== '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const trimmedUrl = url.trim();
    if (trimmedUrl !== '') {
      let ok = false;
      try {
        const parsed = new URL(trimmedUrl);
        ok = parsed.protocol === 'https:' || parsed.protocol === 'http:';
      } catch {
        ok = false;
      }
      if (!ok) {
        setUrlError('Informe uma URL válida começando com https://');
        return;
      }
    }
    setUrlError(null);

    const body: VaultEntryBody = {
      name: name.trim(),
      description: description.trim() || null,
      username: username.trim() || null,
      url: trimmedUrl || null,
      ip: ip.trim() || null,
      folderId: folderId !== '' ? Number(folderId) : null,
      clientId: client?.id ?? null,
      // No edit, omitir `secret` mantém a senha atual.
      ...(secret !== '' ? { secret } : {}),
    };
    if (isEdit) update.mutate({ entryId: initial.id, body }, { onSuccess: close });
    else create.mutate(body, { onSuccess: close });
  };

  return (
    <Dialog
      open
      onClose={close}
      title={isEdit ? 'Editar senha' : 'Nova senha'}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="entry-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="entry-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: SSH produção, Painel do banco…"
            required
            autoFocus={!isEdit}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="entry-description">Descrição</Label>
          <Textarea
            id="entry-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="entry-username">Usuário</Label>
            <Input
              id="entry-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="entry-secret">
                Senha {isEdit ? null : <span className="text-destructive">*</span>}
              </Label>
              <GeneratorPopover
                onGenerate={(pwd) => {
                  setSecret(pwd);
                  setShowSecret(true);
                }}
              />
            </div>
            <div className="relative">
              <Input
                id="entry-secret"
                type={showSecret ? 'text' : 'password'}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder={isEdit ? '••••••• (manter atual)' : ''}
                required={!isEdit}
                autoComplete="new-password"
                spellCheck={false}
                className="pr-9 font-mono"
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                aria-label={showSecret ? 'Ocultar senha' : 'Mostrar senha'}
                title={showSecret ? 'Ocultar senha' : 'Mostrar senha'}
                className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              >
                {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            {secret !== '' ? <StrengthBar secret={secret} /> : null}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="entry-url">URL</Label>
            <Input
              id="entry-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              inputMode="url"
              spellCheck={false}
              aria-invalid={urlError !== null || undefined}
            />
            {urlError !== null ? <p className="text-xs text-destructive">{urlError}</p> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="entry-ip">IP</Label>
            <Input
              id="entry-ip"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              placeholder="Ex.: 10.0.0.15"
              spellCheck={false}
              className="tabular-nums"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="entry-folder">Pasta</Label>
          <Select
            id="entry-folder"
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
          >
            <option value="">Raiz do cofre</option>
            {folderOptions(folders).map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Cliente</Label>
          <ClientPicker selected={client} onSelect={setClient} />
        </div>

        {mutation.error ? (
          <p className="text-sm text-destructive" role="alert">
            {vaultErrorMessage(mutation.error)}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={mutation.isPending || !valid}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
